import React, { useEffect, useState } from "react";
import { X, Megaphone } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { fetchPromoBanner } from "@/lib/api";

const DISMISS_KEY = "nn_promo_dismissed_id";

/**
 * Site-wide dismissible promo bar. Fetches from /api/promo-banner.
 * Reappears when admin publishes a new banner (identity via message hash).
 */
export default function PromoBanner() {
    const { data } = useQuery({
        queryKey: ["public-promo"],
        queryFn: fetchPromoBanner,
        refetchInterval: 5 * 60 * 1000,
    });
    const banner = data?.banner;
    const bannerId = banner ? `${banner.message}:${banner.link_url}` : "";
    const [hidden, setHidden] = useState(false);

    useEffect(() => {
        if (!banner) return;
        try {
            const dismissed = localStorage.getItem(DISMISS_KEY);
            setHidden(dismissed === bannerId);
        } catch { setHidden(false); }
    }, [banner, bannerId]);

    if (!banner || hidden) return null;

    const dismiss = () => {
        try { localStorage.setItem(DISMISS_KEY, bannerId); } catch { /* ignore quota */ }
        setHidden(true);
    };

    return (
        <div
            className="w-full px-4 py-2.5 flex items-center gap-3 relative"
            style={{ background: banner.bg_color || "#22E578", color: banner.text_color || "#000000" }}
            data-testid="site-promo-banner"
        >
            <div className="max-w-[1400px] mx-auto flex items-center gap-3 flex-wrap w-full">
                <Megaphone className="w-4 h-4 shrink-0" />
                <span className="font-sub text-sm flex-1 min-w-0">{banner.message}</span>
                {banner.link_url && banner.link_text && (
                    <a
                        href={banner.link_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-sub text-[11px] uppercase tracking-[0.18em] font-bold px-3 py-1.5 border border-current rounded-sm hover:bg-black/10 transition-colors"
                        data-testid="promo-cta"
                    >
                        {banner.link_text} →
                    </a>
                )}
                {banner.dismissible !== false && (
                    <button
                        onClick={dismiss}
                        aria-label="Dismiss"
                        className="p-1 rounded-full hover:bg-black/10 transition-colors"
                        data-testid="promo-dismiss"
                    >
                        <X className="w-4 h-4" />
                    </button>
                )}
            </div>
        </div>
    );
}
